// =====================================================================
//  Jetsport LCA Generator – TypeScript port of lca.js
//
//  Computes "Vugge til port" GWP-fossil [kg CO2-eq.] for four product
//  types: Broderi, Direkte Tryk, Transfer, Digital Transfer.
//
//  All constants, formulas, and Excel quirks are preserved 1:1 from the
//  certified reference implementation.
// =====================================================================

// ── Payload types ──

interface BroderiPayload {
  Type: "Broderi";
  Amount: number;
  SizeCM2: number;
  "Number of stitches per cm2": number;
  Avalon: boolean;
}

interface DirekteTrykPayload {
  Type: "Direkte Tryk";
  Amount: number;
  SizeCM2: number;
  ColorCount: number;
  DarkTextile: boolean;
}

interface TransferPayload {
  Type: "Transfer";
  Amount: number;
  SizeCM2: number;
  ColorCount: number;
  White: boolean;
  Blokker: boolean;
  Lim: boolean;
  "Logos per sheet": number;
}

interface DigitalTransferPayload {
  Type: "Digital Transfer";
  Amount: number;
  SizeCM2: number;
  ColorCount: number;
  White: boolean;
  Blokker: boolean;
  Lim: boolean;
  "Logos per sheet": number;
}

export type LcaPayload =
  | BroderiPayload
  | DirekteTrykPayload
  | TransferPayload
  | DigitalTransferPayload;

export interface LcaResult {
  VuggeTilPortPerLogo: number;
  VuggeTilPortTotal: number;
  breakdown: Record<string, number>;
}

// ── NRGi material rows 8-26: D = (1+pack frac), G = GWP-fossil/kg ──
const NRGI_MAT_D: Record<number, number> = {
  8: 1.2475333333333334,    9: 1.2302,            10: 1.4768,
  11: 1.0859,               12: 1.1458,           13: 1.2,
  14: 1.1109,               15: 1.054,            16: 1.09,
  17: 1.0533,               18: 1.04,             19: 1.1303333333333334,
  20: 3,                    21: 1.0436,           22: 1.08,
  23: 1.07,                 24: 1.01,             25: 1.074,
  26: 1.20571,
};
const NRGI_MAT_G: Record<number, number> = {
  8:  6.747807168822989,    9:  6.727938117189536,
  10: 6.0610418577532235,   11: 6.486454317261364,
  12: 0.141050250793552,    13: 2.144379304128,
  14: 3.0616822098629157,   15: 2.6215219389042397,
  16: 3.1906634040800004,   17: 0.056693932900352004,
  18: 0.6994782805024,      19: 7.35403423677392,
  20: 14.79569012,          21: 6.7273570597128,
  22: 3.16147934496,        23: 3.13229528584,
  24: 6.3075701980756,      25: 6.47610690017928,
  26: 3.092084744601706,
};

// ── NRGi packaging waste rows 45-63 ──
const NRGI_PACK_D: Record<number, number> = {
  45: 0.24753333333333333,  46: 0.23020000000000002,
  47: 0.4768,               48: 0.0859,
  49: 0.1458,               50: 0.2,
  51: 0.1109,               52: 0.054,
  53: 0.09,                 54: 0.0533,
  55: 0.04,                 56: 0.13033333333333333,
  57: 2,                    58: 0.0436,
  59: 0.08,                 60: 0.07,
  61: 0.01,                 62: 0.074,
  63: 0.20571,
};
// F = GWP-total of pack waste — referenced ONLY by DT F48 quirk
const NRGI_PACK_F: Record<number, number> = { 54: 0.1306488689232646 };
const NRGI_PACK_G: Record<number, number> = {
  45: 0.07100132043493335,  46: 0.0704142886696,
  47: 0.0792410070864,      48: 0.1625256651912,
  49: 0.0360126073184,      50: 0.038453509100000005,
  51: 0.06478603681926001,  52: 0.033115377092,
  53: 0.034431628820000004, 54: 0.032921909628400005,
  55: 0.032477517420000004, 56: 0.06423996074933333,
  57: 4.7906653995,         58: 0.10135172023388,
  59: 0.034066003340000005, 60: 0.033700377860000005,
  61: 0.05493862150000001,  62: 0.033846628052000005,
  63: 0.06964580074908,
};

// ── NRGi energy + water + transport ──
const NRGI_G37 = 1.6484834403611126e-05;   // Gas per cm2
const NRGI_G38 = 0.00023929733912200052;   // El per cm2 (faelles)
const NRGI_G39 = 2.4060583285379313e-06;   // El per cm2 (Transfer)
const NRGI_G40 = 1.9487930341164738e-05;   // El per stitch (Broderi)
const NRGI_G41 = 0.00037257979;            // Vand per kg
const NRGI_G42 = 0.00011453569505488452;   // El per cm2 (DT)
const NRGI_G67 = 0.27369524;               // Waste water treatment
const NRGI_F84 = 0.01437731073192;         // C2 transport (uses GWP-total)

// ── NRGi C3+C4 incineration (G column, GWP-fossil) ──
const NRGI_C3_G: Record<string, number> = {
  "Overtråd, 100% polyester":      0.75880681,
  "Undertråd":                     0.75880681,
  "Symaskine nåle":                0.018149783,
  "Vlies":                         0.75880681,
  "Avalon":                        2.3797622,
  "Symaskine olie":                1.13,
  "Fote Coat - Emulsion":          1.13,
  "Epic base - Backstop":          1.13,
  "Direkte tryk - farve":          1.13,
  "Tape, PVC":                     2.3797622,
  "Papir til pallet":              0.51846346,
  "Lim til pallet":                1.13,
  "Mesh til rammer, polyester":    0.75880681,
  "Transfer film - Ark, polyester": 2.3797622,
  "Transfer farve":                1.13,
  "Transfer Blok":                 1.13,
  "Limpulver":                     1.13,
  "Lim":                           1.13,
  "Digital transfer farve":        1.13,
};

// ── Per-product sheet constants ──
// Broderi
const BR_E18 = 1.2795414462081128e-07;
const BR_E19 = 4.586640211640212e-08;
const BR_E33 = 4.641532911342566e-05;
const BR_E34 = 4.4094562657754374e-05;
const BR_E35 = 8.75885798197776e-09;
const BR_E36 = 9.61138682555693e-07;

// Direkte Tryk
const DT_D18 = 6.01296706846337e-06;
const DT_D26 = 4.3462980264598614e-06;
const DT_D33 = 0.0003110277489721049;
const DT_D34 = 0.00020075427433654042;
const DT_VAND_LITER  = 39;
const DT_BACKEM_GRAM = 42;
const DT_TAPE_GRAM   = 78.912;
const DT_MESH_GRAM   = 15.960685714285715;
const DT_COLEM_GRAM  = 27;

// Transfer
const TR_FARVE_GRAM_ARK   = 17.23;
const TR_EMUL_GRAM_RAMME  = 50;
const TR_TAPE_GRAM_RAMME  = 103.68;
const TR_MESH_GRAM_RAMME  = 15.960685714285715;
const TR_VAND_LITER       = 39;
const TR_HVID_EMUL_GRAM   = 91;
const TR_BLOK_GRAM_ARK    = 13.5;
const TR_LIM_GRAM_ARK     = 5.59;
const TR_LIMEM_GRAM_RAMME = 208;
const TR_FILM_KG_PER_ARK  = 3500 * (0.1 / 10) * 1.39 / 1000;  // 0.04865

// ── Helper: F-cell formula (C * D + C) * G ──
const fMat = (c: number, d: number, g: number) => (c * d + c) * g;

// ── Broderi ──
function calcBroderi(input: BroderiPayload): LcaResult {
  const a = input.SizeCM2;
  const n = input.Amount;
  const s = input["Number of stitches per cm2"];
  const avalon = !!input.Avalon;

  const C9  = s * a * BR_E18;
  const C10 = s * a * BR_E19;
  const C11 = s * a * BR_E35;
  const C12 = a * BR_E33;
  const C13 = avalon ? a * BR_E34 : 0;
  const C14 = s * a * BR_E36;

  const F9  = fMat(C9,  NRGI_PACK_D[45], NRGI_MAT_G[8]);
  const F10 = fMat(C10, NRGI_PACK_D[46], NRGI_MAT_G[9]);
  const F11 = fMat(C11, NRGI_PACK_D[47], NRGI_MAT_G[10]);
  const F12 = fMat(C12, NRGI_PACK_D[48], NRGI_MAT_G[11]);
  const F13 = fMat(C13, NRGI_PACK_D[49], NRGI_MAT_G[12]);

  const F17 = a * NRGI_G37;
  const F18 = a * NRGI_G38;
  const F19 = s * a * NRGI_G40;

  const F22 =
      C9  * NRGI_PACK_D[45] * NRGI_PACK_G[45]
    + C10 * NRGI_PACK_D[46] * NRGI_PACK_G[46]
    + C11 * NRGI_PACK_D[47] * NRGI_PACK_G[47]
    + C12 * NRGI_PACK_D[48] * NRGI_PACK_G[48]
    + C13 * NRGI_PACK_D[49] * NRGI_PACK_G[49]
    + C14 * NRGI_PACK_D[50] * NRGI_PACK_G[50];

  const F37 = NRGI_C3_G["Symaskine nåle"] * C11;
  const F40 = NRGI_C3_G["Symaskine olie"] * C14;
  const D23 = C11 + C14;
  const F23 = (F37 + F40) + D23 * NRGI_F84;

  const perLogo = F9 + F10 + F11 + F12 + F13;
  return {
    VuggeTilPortPerLogo: perLogo,
    VuggeTilPortTotal: perLogo * n,
    breakdown: {F9, F10, F11, F12, F13, F17, F18, F19, F22, F23},
  };
}

// ── Direkte Tryk ──
function calcDirekteTryk(input: DirekteTrykPayload): LcaResult {
  const a = input.SizeCM2;
  const n = input.Amount;
  const cc = input.ColorCount;
  const dark = !!input.DarkTextile;

  const J19 = n * a;
  const D17 = DT_VAND_LITER  / J19;
  const D20 = DT_BACKEM_GRAM / J19 / 1000;
  const D21 = DT_TAPE_GRAM   / J19 / 1000;
  const D22 = DT_MESH_GRAM   / J19 / 1000;
  const D25 = DT_VAND_LITER  / J19;
  const D28 = DT_COLEM_GRAM  / J19 / 1000;
  const D29 = DT_TAPE_GRAM   / J19 / 1000;
  const D30 = DT_MESH_GRAM   / J19 / 1000;

  const C44 = cc * (a * D25) + (dark ? a * D17 : 0);
  const C45 = dark ? a * DT_D18 : 0;
  const C46 = cc * a * DT_D26;
  const C47 = cc * (a * D28) + (dark ? a * D20 : 0);
  const C48 = cc * (a * D29) + (dark ? a * D21 : 0);
  const C49 = cc * (a * D30) + (dark ? a * D22 : 0);
  const C50 = DT_D33 * a;
  const C51 = DT_D34 * a;

  const F44 = C44 * NRGI_G41;
  const F45 = fMat(C45, NRGI_PACK_D[52], NRGI_MAT_G[15]);
  const F46 = fMat(C46, NRGI_PACK_D[53], NRGI_MAT_G[16]);
  const F47 = fMat(C47, NRGI_PACK_D[51], NRGI_MAT_G[14]);
  // F48 quirk: uses NRGI.F54 (= GWP-total of Tape pack waste) instead of D54
  const F48 = (C48 * NRGI_PACK_F[54] + C48) * NRGI_MAT_G[17];
  const F49 = fMat(C49, NRGI_PACK_D[57], NRGI_MAT_G[20]);
  const F50 = fMat(C50, NRGI_PACK_D[55], NRGI_MAT_G[18]);
  const F51 = fMat(C51, NRGI_PACK_D[62], NRGI_MAT_G[25]);

  const F54 = a * NRGI_G37;
  const F55 = a * NRGI_G38;
  const F56 = a * NRGI_G42;

  const F59 =
      C45 * NRGI_PACK_D[52] * NRGI_PACK_G[52]
    + C46 * NRGI_PACK_D[53] * NRGI_PACK_G[53]
    + C47 * NRGI_PACK_D[51] * NRGI_PACK_G[51]
    + C48 * NRGI_PACK_D[54] * NRGI_PACK_G[54]
    + C49 * NRGI_PACK_D[57] * NRGI_PACK_G[57]
    + C50 * NRGI_PACK_D[55] * NRGI_PACK_G[55]
    + C51 * NRGI_PACK_D[56] * NRGI_PACK_G[56];

  const F60 = (C44 / 1000) * NRGI_G67;

  const F73 = NRGI_C3_G["Fote Coat - Emulsion"]      * C47;
  const F74 = NRGI_C3_G["Tape, PVC"]                 * C48;
  const F75 = NRGI_C3_G["Mesh til rammer, polyester"] * C49;
  const F76 = NRGI_C3_G["Papir til pallet"]          * C50;
  const F77 = NRGI_C3_G["Lim"]                       * C51;
  const D61 = C47 + C48 + C49 + C50 + C51;
  const F61 = (F73 + F74 + F75 + F76 + F77) + D61 * NRGI_F84;

  const perLogo = F44 + F45 + F46 + F47 + F48 + F49 + F50 + F51;
  return {
    VuggeTilPortPerLogo: perLogo,
    VuggeTilPortTotal: perLogo * n,
    breakdown: {F44, F45, F46, F47, F48, F49, F50, F51, F54, F55, F56, F59, F60, F61},
  };
}

// ── Transfer / Digital Transfer share dynamic D-values ──
function transferD(amount: number, sizeCm2: number, logosPerSheet: number): Record<number, number> {
  const F8  = logosPerSheet * sizeCm2;
  const F12 = amount * sizeCm2;
  return {
    18: TR_FARVE_GRAM_ARK / 1000 / F8,
    21: TR_VAND_LITER / F12,
    23: TR_EMUL_GRAM_RAMME / 1000 / F12,
    24: TR_TAPE_GRAM_RAMME / 1000 / F12,
    25: TR_MESH_GRAM_RAMME / 1000 / F12,
    28: TR_VAND_LITER / F12,
    29: TR_FARVE_GRAM_ARK / 1000 / F8,
    31: TR_HVID_EMUL_GRAM / 1000 / F12,
    32: TR_TAPE_GRAM_RAMME / 1000 / F12,
    33: TR_MESH_GRAM_RAMME / 1000 / F12,
    37: TR_BLOK_GRAM_ARK / 1000 / F8,
    39: TR_TAPE_GRAM_RAMME / 1000 / F12,
    40: TR_VAND_LITER / F12,
    41: TR_MESH_GRAM_RAMME / 1000 / F12,
    45: TR_LIM_GRAM_ARK / 1000 / F8,
    46: TR_LIMEM_GRAM_RAMME / 1000 / F12,
    47: TR_TAPE_GRAM_RAMME / 1000 / F12,
    48: TR_VAND_LITER / F12,
    49: TR_MESH_GRAM_RAMME / 1000 / F12,
    52: TR_FILM_KG_PER_ARK / F8,
  };
}

// ── Transfer ──
function calcTransfer(input: TransferPayload): LcaResult {
  const a = input.SizeCM2;
  const n = input.Amount;
  const cc = input.ColorCount;
  const white = !!input.White;
  const blokker = !!input.Blokker;
  const lim = !!input.Lim;
  const D = transferD(n, a, input["Logos per sheet"]);

  const C82 = cc * (a * D[21])
            + (white ? a * D[28] : 0)
            + (blokker ? a * D[40] : 0)
            + (lim ? a * D[48] : 0);
  const C83 = cc * (a * D[18]) + (white ? a * D[29] : 0);
  const C84 = cc * (a * D[23])
            + (white ? a * D[31] : 0)
            + (lim ? a * D[46] : 0);
  const C85 = cc * (a * D[24])
            + (white ? a * D[32] : 0)
            + (blokker ? a * D[39] : 0)
            + (lim ? a * D[47] : 0);
  const C86 = cc * (a * D[25])
            + (white ? a * D[33] : 0)
            + (blokker ? a * D[41] : 0)
            + (lim ? a * D[49] : 0);
  const C87 = blokker ? a * D[37] : 0;
  const C88 = lim ? a * D[45] : 0;
  const C89 = D[52] * a;

  const F82 = C82 * NRGI_G41;
  const F83 = fMat(C83, NRGI_MAT_D[22], NRGI_MAT_G[22]);
  const F84v = fMat(C84, NRGI_MAT_D[14], NRGI_MAT_G[14]);
  const F85 = fMat(C85, NRGI_MAT_D[17], NRGI_MAT_G[17]);
  const F86 = fMat(C86, NRGI_MAT_D[20], NRGI_MAT_G[20]);
  const F87 = fMat(C87, NRGI_MAT_D[23], NRGI_MAT_G[23]);
  const F88 = fMat(C88, NRGI_MAT_D[24], NRGI_MAT_G[24]);
  const F89 = fMat(C89, NRGI_MAT_D[21], NRGI_MAT_G[21]);

  const F92 = a * NRGI_G37;
  const F93 = a * NRGI_G38;
  const F94 = a * NRGI_G39;

  const F97 =
      C83 * NRGI_PACK_D[59] * NRGI_PACK_G[59]
    + C84 * NRGI_PACK_D[51] * NRGI_PACK_G[51]
    + C85 * NRGI_PACK_D[54] * NRGI_PACK_G[54]
    + C86 * NRGI_PACK_D[57] * NRGI_PACK_G[57]
    + C87 * NRGI_PACK_D[60] * NRGI_PACK_G[60]
    + C88 * NRGI_PACK_D[62] * NRGI_PACK_G[62]
    + C89 * NRGI_PACK_D[58] * NRGI_PACK_G[58];
  const F98 = (C82 / 1000) * NRGI_G67;

  const F110 = NRGI_C3_G["Fote Coat - Emulsion"] * C84;
  const F111 = NRGI_C3_G["Tape, PVC"] * C85;
  const F112 = NRGI_C3_G["Mesh til rammer, polyester"] * C86;
  const D99 = C84 + C85 + C86;
  const F99 = (F110 + F111 + F112) + D99 * NRGI_F84;

  const perLogo = F82 + F83 + F84v + F85 + F86 + F87 + F88 + F89;
  return {
    VuggeTilPortPerLogo: perLogo,
    VuggeTilPortTotal: perLogo * n,
    breakdown: {F82, F83, F84: F84v, F85, F86, F87, F88, F89, F92, F93, F94, F97, F98, F99},
  };
}

// ── Digital Transfer ──
function calcDigitalTransfer(input: DigitalTransferPayload): LcaResult {
  const a = input.SizeCM2;
  const n = input.Amount;
  const cc = input.ColorCount;
  const white = !!input.White;
  const blokker = !!input.Blokker;
  const lim = !!input.Lim;
  const D = transferD(n, a, input["Logos per sheet"]);

  // Transfer block C-cells (referenced by F134 in Calculations sheet)
  const C83 = cc * (a * D[18]) + (white ? a * D[29] : 0);
  const C84 = cc * (a * D[23])
            + (white ? a * D[31] : 0)
            + (lim ? a * D[46] : 0);
  const C85 = cc * (a * D[24])
            + (white ? a * D[32] : 0)
            + (blokker ? a * D[39] : 0)
            + (lim ? a * D[47] : 0);
  const C86 = cc * (a * D[25])
            + (white ? a * D[33] : 0)
            + (blokker ? a * D[41] : 0)
            + (lim ? a * D[49] : 0);
  const C87 = blokker ? a * D[37] : 0;
  const C88 = lim ? a * D[45] : 0;
  const C89 = D[52] * a;

  // Digital Transfer block C-cells
  const C119 = (white ? a * D[28] : 0)
             + (blokker ? a * D[40] : 0)
             + (lim ? a * D[48] : 0);
  const C120 = cc * (a * D[18]) + (white ? a * D[29] : 0);
  const C121 = (white ? a * D[31] : 0) + (lim ? a * D[46] : 0);
  const C122 = (white ? a * D[32] : 0)
             + (blokker ? a * D[39] : 0)
             + (lim ? a * D[47] : 0);
  const C123 = (white ? a * D[33] : 0)
             + (blokker ? a * D[41] : 0)
             + (lim ? a * D[49] : 0);
  const C124 = blokker ? a * D[37] : 0;
  const C125 = lim ? a * D[45] : 0;
  const C126 = D[52] * a;

  const F119 = C119 * NRGI_G41;
  const F120 = fMat(C120, NRGI_MAT_D[26], NRGI_MAT_G[26]);
  const F121 = fMat(C121, NRGI_MAT_D[14], NRGI_MAT_G[14]);
  const F122 = fMat(C122, NRGI_MAT_D[17], NRGI_MAT_G[17]);
  const F123 = fMat(C123, NRGI_MAT_D[20], NRGI_MAT_G[20]);
  const F124 = fMat(C124, NRGI_MAT_D[23], NRGI_MAT_G[23]);
  const F125 = fMat(C125, NRGI_MAT_D[24], NRGI_MAT_G[24]);
  const F126 = fMat(C126, NRGI_MAT_D[21], NRGI_MAT_G[21]);

  const F129 = a * NRGI_G37;
  const F130 = a * NRGI_G38;
  const F131 = a * NRGI_G39;

  // F134: references Transfer C83-C89 + uses material D26/G26 for first term.
  const F134 =
      C83 * NRGI_MAT_D[26] * NRGI_MAT_G[26]
    + C84 * NRGI_PACK_D[51] * NRGI_PACK_G[51]
    + C85 * NRGI_PACK_D[54] * NRGI_PACK_G[54]
    + C86 * NRGI_PACK_D[57] * NRGI_PACK_G[57]
    + C87 * NRGI_PACK_D[60] * NRGI_PACK_G[60]
    + C88 * NRGI_PACK_D[62] * NRGI_PACK_G[62]
    + C89 * NRGI_PACK_D[58] * NRGI_PACK_G[58];
  const F135 = (C119 / 1000) * NRGI_G67;

  const F147 = NRGI_C3_G["Fote Coat - Emulsion"] * C121;
  const F148 = NRGI_C3_G["Tape, PVC"] * C122;
  const F149 = NRGI_C3_G["Mesh til rammer, polyester"] * C123;
  const D136 = C121 + C122 + C123;
  const F136 = (F147 + F148 + F149) + D136 * NRGI_F84;

  const perLogo = F119 + F120 + F121 + F122 + F123 + F124 + F125 + F126;
  return {
    VuggeTilPortPerLogo: perLogo,
    VuggeTilPortTotal: perLogo * n,
    breakdown: {F119, F120, F121, F122, F123, F124, F125, F126, F129, F130, F131, F134, F135, F136},
  };
}

// ── Public API ──
export function calculate(payload: LcaPayload): LcaResult {
  switch (payload.Type) {
    case "Broderi":          return calcBroderi(payload);
    case "Direkte Tryk":     return calcDirekteTryk(payload);
    case "Transfer":         return calcTransfer(payload);
    case "Digital Transfer": return calcDigitalTransfer(payload);
  }
}
